import React from 'react';
import { TrendingUp, Truck, Package, CheckCircle, Clock, AlertCircle, MapPin, X, ChevronRight, Download, Settings, Loader, RotateCcw } from 'lucide-react';
import '../../../../styles/components/loading-dashboard.scss';
import HeaderBanner from '../../../common/HeaderBanner';
import { SideBar } from '../../sidebar';
import FilterSection from '../../../common/Filter';
import i18n from '../../../../i18n';
import { getLoadingProgress } from './services/LoadingProgress';
import { toast } from 'react-toastify';
import {  formatWeight } from '../../../../utils/common';
import { getAuctionList, getGroupsSelectors, getOrganizationSelectors } from '../LoadingManagement/services/LoadingManagementService';
import { exportWeighbridgeTickets } from '../WeighbridgeTicketList/services/WeighbridgeTicketService';
import { getLoggedUser } from '../../../../state/user';

// Feature Flags Manager
class FeatureFlags {
  static DEFAULT_FLAGS = {
    showModal: true,
    showTruckAnimation: true,
    showExportButtons: true,
    showStatusBadges: true,
    showProgressBars: false,
    showLastActivity: true
  };

  static getFlags() {
    const saved = localStorage.getItem('dashboardFeatureFlags');
    return saved ? JSON.parse(saved) : this.DEFAULT_FLAGS;
  }

  static saveFlags(flags) {
    localStorage.setItem('dashboardFeatureFlags', JSON.stringify(flags));
  }

  static resetFlags() {
    localStorage.removeItem('dashboardFeatureFlags');
    return this.DEFAULT_FLAGS;
  }
}
const user = getLoggedUser();

class LoadingProgressDashboard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedSite: null,
      timeRange: 'today',
      hideSidebar: false,
      showFlagSettings: false,
      featureFlags: FeatureFlags.getFlags(),
      loading: false,
      error: null,

      client: "",
      clients: [],
      groupName: "",
      groups: [],
      auction: "",
      auctions: [],

      loadingClients: false,
      loadingGroups: false,
      loadingAuctions: false,
      is_exporting: false,

      // API Response Data
      sites: [],
      overallStats: {
        total_sites: 0,
        total_planned_weight: 0,
        total_collected_weight: 0,
        total_remaining_weight: 0,
        average_progress: 0,
        sites_by_status: {
          P: 0,
          T: 0,
          C: 0,
          D: 0
        }
      },

      // Filter state
      filters: {
        site_id: '',
        status: '',
        auction: '',
        date_from: '',
        date_to: '',
        search: '',
        per_page: 15,
        page: 1
      },

      exportFilters: {
        start_date: '',
        end_date: '',
        delivery_site_id: '',
        status: 'OUT',
        export_excel: true
      },

      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
      }
    };
  }

  componentDidMount() {
    if (['C'].includes(user.role)) {
      this.setState({ client: user.id });
      this.fetchGroupsList(user.id);
    
    } else {
      this.fetchAllOrganization();
    }
    this.fetchLoadingProgress();
  }

  fetchLoadingProgress = async () => {
    this.setState({ loading: true, error: null });

    try {
      let filters = { ...this.state.filters }; // Create a base filters object

      // Always add auction_id if selected
      if (this.state.auction) {
        filters.auction_id = this.state.auction;
      }

      // Add user_id based on role
      if (user && user.role && user.role === 'C') {
        filters.user_id = user.id;
      }

      const response = await getLoadingProgress(filters);

      if (response.success) {
        this.setState({
          sites: response.data || [],
          overallStats: response.overall_stats || this.state.overallStats,
          pagination: response.meta || this.state.pagination,
          loading: false
        });
      } else {
        this.setState({
          error: response.message || 'Failed to fetch data',
          loading: false
        });
        toast.error(response.message || 'Failed to fetch data', 'error');
      }
    } catch (error) {
      this.setState({
        error: error.message || 'Something went wrong',
        loading: false
      });
      toast.error('Error fetching loading progress', 'error');
    }
  };

  fetchAllOrganization = async () => {
    this.setState({ loadingClients: true });

    try {
      const response = await getOrganizationSelectors();
      if (response.success && response.data) {
        const clients = response.data.map(client => ({
          id: client.id,
          value: client.id,
          label: client.organization_name,
          organization_name: client.organization_name,
        }));

        this.setState({
          clients,
          loadingClients: false
        });
      } else {
        toast.error(response.message || 'Failed to fetch organization');
        this.setState({ loadingClients: false });
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      toast.error('Error loading organization list');
      this.setState({ loadingClients: false });
    }
  }

  fetchGroupsList = async (organizationId) => {
    this.setState({ loadingGroups: true });

    try {
      const filters = {
        organization_id: organizationId // Pass organization_id to filter
      };

      const response = await getGroupsSelectors(filters);

      if (response.success && response.data) {
        const groups = response.data.map(group => ({
          id: group.id,
          value: group.id,
          label: group.group_name,
          name: group.group_name,
          organization_id: group.organization_id,
        }));

        this.setState({
          groups,
          loadingGroups: false
        });
      } else {
        toast.error(response.message || 'Failed to fetch groups');
        this.setState({ loadingGroups: false, groups: [] });
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Error loading groups list');
      this.setState({
        loadingGroups: false,
        groups: [],
        groupName: '',
        groupActualName: ''
      });
    }
  }

  fetchAuctionList = async (groupId) => {
    this.setState({ loadingAuctions: true }); // Changed from loadingAuction to loadingAuctions

    try {
      const filters = { groupId: groupId };
      const response = await getAuctionList(filters);

      if (response.success && response.data) {
        // Transform the data to include winners information
        const auctions = response.data.map(auction => ({
          id: auction.id,
          value: auction.id,
          label: `${auction.title}`,
          name: auction.title,
          winners: auction.winners || [] // This will always have one winner
        }));

        this.setState({
          auctions,
          loadingAuctions: false // Changed from loadingAuction
        });
      } else {
        toast.error(response.message || 'Failed to fetch auction');
        this.setState({ loadingAuctions: false }); // Changed from loadingAuction
      }
    } catch (error) {
      console.error('Error fetching auction:', error);
      toast.error('Error loading auction list');
      this.setState({
        loadingAuctions: false, // Changed from loadingAuction
        auctions: []
      });
    }
  };

  handleClientChange = (e) => {
    const clientId = e.target.value;
    const selectedClient = this.state.clients.find(c => c.id == clientId);

    this.setState(prevState => ({
      client: clientId,
      groups: [], // Clear groups when client changes
      groupName: '',
      groupActualName: '',
      auctions: [], // Clear auctions data
      errors: {
        ...prevState.errors,
        client: '',
        groupName: '',
        groupActualName: ''
      },
    }));

    // Fetch groups for the selected organization
    if (clientId) {
      this.fetchGroupsList(clientId);
      if (selectedClient) {
        toast.success(`Client "${selectedClient.organization_name}" selected. Loading groups...`);
      }
    }
  };

  handleGroupChange = (e) => {
    const groupId = e.target.value;
    const selectedGroup = this.state.groups.find(g => g.id == groupId);

    this.setState(prevState => ({
      groupName: groupId,
      groupActualName: selectedGroup ? selectedGroup.label : '', // Auto-fill with the selected label
      errors: {
        ...prevState.errors,
        groupName: '',
        groupActualName: ''
      },
      auctions: [], // Clear previous auctions
    }));

    // Fetch auctions for the selected group
    if (groupId) {
      this.fetchAuctionList(groupId);
      if (selectedGroup) {
        toast.success(`Group "${selectedGroup.label}" selected. Loading auctions...`);
      }
    }
  };

  handleAuctionChange = (auctionId) => {
    // Find the selected auction
    const selectedAuction = this.state.auctions.find(a => a.id == auctionId);

    this.setState(prevState => ({
      auction: auctionId,
      // Don't clear auctions here - keep them for the dropdown
      errors: {
        ...prevState.errors,
        auction: ''
      }
    }), () => {
      // Optional: Trigger data fetch with the selected auction
      this.fetchLoadingProgress();
    });
  };


  exportToExcel = async (siteId) => {
    this.setState({ is_exporting: true });

    try {
      // Get the selected site data
      const { selectedSite } = this.state;

      // Initialize date variables
      let startDate = '';
      let endDate = '';

      // Check if recent_tickets exists and has tickets
      if (selectedSite?.breakdowns?.recent_tickets &&
        selectedSite.breakdowns.recent_tickets.length > 0) {

        // Get all valid dates from tickets
        const validDates = selectedSite.breakdowns.recent_tickets
          .map(ticket => ticket.created_at ? new Date(ticket.created_at) : null)
          .filter(date => date !== null && !isNaN(date.getTime()));

        if (validDates.length > 0) {
          // Sort dates to get earliest and latest
          validDates.sort((a, b) => a - b);

          // Get first and last dates
          const firstDate = validDates[0];
          const lastDate = validDates[validDates.length - 1];

          // Format dates as YYYY-MM-DD
          startDate = firstDate.toISOString().split('T')[0];
          endDate = lastDate.toISOString().split('T')[0];
        }
      }

      // If no valid dates found, use current date as fallback
      if (!startDate || !endDate) {
        const today = new Date().toISOString().split('T')[0];
        startDate = today;
        endDate = today;
        toast.info('No ticket dates found. Using current date for export.');
      }

      // Prepare filters with the extracted dates
      const filters = {
        delivery_site_id: siteId,
        start_date: startDate,
        end_date: endDate,
        status: 'OUT', // or 'all' if you want all statuses
        export_excel: true
      };

      console.log('Exporting with filters:', filters);

      const response = await exportWeighbridgeTickets(filters);

      console.log('Export response:', response);

      if (response.data) {
        // If the API returns a blob (Excel file)
        if (response.data instanceof Blob) {
          // Check if it's an error JSON
          if (response.data.type === 'application/json') {
            const text = await response.data.text();
            try {
              const errorData = JSON.parse(text);
              toast.error(errorData.message || 'Failed to export report');
            } catch (e) {
              toast.error('Failed to export report');
            }
            return;
          }

          // Create download link
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;

          // Generate filename with date range
          const filename = `COLLECTION_REPORT_${startDate}_TO_${endDate}.xlsx`;
          link.setAttribute('download', filename);

          document.body.appendChild(link);
          link.click();
          link.remove();

          // Clean up URL
          window.URL.revokeObjectURL(url);

          toast.success(`Report exported successfully for period ${startDate} to ${endDate}`);
        } else {
          // If API returns JSON data (for manual Excel generation)
          this.generateExcelFromData(response.data);
        }

        // Close modal on success (optional)
        // this.setState({ selectedSite: null });
      } else {
        console.error('Export failed:', response.message);
        toast.error('Failed to export report: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error exporting report:', error);

      // Handle specific error messages
      if (error.response?.status === 404) {
        toast.error('No tickets found for this site');
      } else if (error.response?.status === 422) {
        toast.error('Invalid date range or parameters');
      } else {
        toast.error(error.message || 'Error exporting report');
      }
    } finally {
      this.setState({ is_exporting: false });
    }
  };

  handleFilterChange = (key, value) => {
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        [key]: value,
        page: 1 // Reset to first page on filter change
      }
    }), () => {
      this.fetchLoadingProgress();
    });
  };

  handleSearchChange = (e) => {
    this.handleFilterChange('search', e.target.value);
  };

  handleStatusFilter = (status) => {
    this.handleFilterChange('status', status);
  };

  handlePageChange = (page) => {
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        page
      }
    }), () => {
      this.fetchLoadingProgress();
    });
  };

  clearFilters = () => {
    this.setState({
      auction: "",
      groupName: "",
      client: "",
      filters: {
        site_id: '',
        auction_id: '',
        status: '',
        search: '',
        per_page: 15,
        page: 1
      }
    }, () => {
        this.fetchLoadingProgress();
    });
  };

  getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'c':
        return <CheckCircle size={16} />;
      case 'in-progress':
      case 't':
        return <Clock size={16} />;
      case 'delayed':
      case 'd':
        return <AlertCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  getStatusDisplay = (status) => {
    const statusMap = {
      'C': 'Completed',
      'T': 'In Progress',
      'P': 'Pending',
      'D': 'Delayed'
    };
    return statusMap[status] || status || 'Unknown';
  };

  getStatusClass = (status) => {
    const statusMap = {
      'C': 'completed',
      'T': 'in-progress',
      'P': 'pending',
      'D': 'delayed'
    };
    return statusMap[status] || 'pending';
  };



  toggleFeatureFlag = (flagName) => {
    this.setState(prevState => {
      const newFlags = {
        ...prevState.featureFlags,
        [flagName]: !prevState.featureFlags[flagName]
      };
      FeatureFlags.saveFlags(newFlags);
      return { featureFlags: newFlags };
    });
  };

  resetFeatureFlags = () => {
    const defaultFlags = FeatureFlags.resetFlags();
    this.setState({ featureFlags: defaultFlags });
  };

  renderSkeletonLoader = () => {
    return (
      <div className="sites-grid">
        {[1, 2, 3, 4].map((n) => (
          <div key={n} className="site-card skeleton-card">
            <div className="skeleton-status-indicator"></div>
            <div className="site-card-header">
              <div>
                <div className="skeleton-title"></div>
                <div className="skeleton-location"></div>
              </div>
              <div className="skeleton-badge"></div>
            </div>
            <div className="skeleton-truck"></div>
            <div className="site-info-grid">
              <div className="skeleton-info"></div>
              <div className="skeleton-info"></div>
            </div>
            <div className="site-card-footer">
              <div className="skeleton-activity"></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  render() {
    const {
      sites,
      selectedSite,
      hideSidebar,
      showFlagSettings,
      featureFlags,
      loading,
      error,
      overallStats,
      filters,
      pagination,
      client,
      clients,
      loadingClients,
      groups,
      loadingGroups,
      groupName,
      loadingAuctions,
      auctions,
      auction
    } = this.state;

    const activeSites = overallStats.sites_by_status?.T || 0;

    return (
      <React.Fragment>
        <HeaderBanner
          title="Loading Progress Dashboard"
          subtitle="Real-time monitoring of auction collection status and progress"
        />

        <section className="mt-5 site-visit-details-page">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!hideSidebar && <SideBar />}
              </div>


              <div className={hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}>
                <div className="loading-dashboard card">
                  <div className="dashboard-container">
                    <div className="dashboard-wrapper">


                      <div className="p-3">

                        <div className="header-content">
                          <div className="header-title-section">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <span onClick={() => this.setState({ hideSidebar: !hideSidebar })}>
                                {hideSidebar ? <i className="icon-size-actual"></i> : <i className="icon-size-fullscreen"></i>}
                              </span>
                              {/* <div>
                                <h1 className="main-title">Loading Progress Dashboard</h1>
                                <p className="main-subtitle">Real-time monitoring of auction collection status</p>
                              </div> */}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            {/* <select
                              value={timeRange}
                              onChange={(e) => this.setState({ timeRange: e.target.value })}
                              className="button1 button1-gray"
                            >
                              <option value="today">Today</option>
                              <option value="week">This Week</option>
                              <option value="month">This Month</option>
                            </select> */}
                            <button
                              onClick={() => this.fetchLoadingProgress()}
                              className="button1 button1-primary"
                            >
                             Refresh <RotateCcw size={20} />
                            </button>

                            <button
                              onClick={() => this.setState({ showFlagSettings: !showFlagSettings })}
                              className="button1 button1-primary"
                            >
                              <Settings size={20} />
                            </button>
                          </div>
                        </div>

                        <FilterSection onClear={this.clearFilters}>

                          {!['C'].includes(user.role) && (
                            <div className="select-wrapper floating-label-wrapper">
                              <select
                                value={client}
                                onChange={this.handleClientChange}
                                disabled={loadingClients}
                                className='filter-base floating-input'
                              >
                                <option value="">Select Client</option>
                                {loadingClients ? (
                                  <option value="" disabled>Loading Clients...</option>
                                ) : (
                                  clients.map(client => (
                                    <option key={client.id} value={client.id}>
                                      {client.label}
                                    </option>
                                  ))
                                )}
                              </select>
                              <label className="floating-label">{i18n.t('organization')}</label>
                            </div>)
                          }

                          <div className="select-wrapper floating-label-wrapper">

                            <select
                              value={groupName}
                              onChange={this.handleGroupChange}
                              className='filter-base floating-input'
                              disabled={loadingGroups || !client}
                            >
                              <option value="">Select Group</option>
                              {!client ? (
                                <option value="" disabled>Select a client first</option>
                              ) : loadingGroups ? (
                                <option value="" disabled>Loading Groups...</option>
                              ) : groups.length === 0 ? (
                                <option value="" disabled>No groups found for this client</option>
                              ) : (
                                groups.map(group => (
                                  <option key={group.id} value={group.id}>
                                    {group.label}
                                  </option>
                                ))
                              )}
                            </select>
                            <label className="floating-label">{i18n.t('project_name')}</label>
                          </div>


                          <div className="select-wrapper floating-label-wrapper">
                            <select
                              id={`auction-select`}
                              value={auction}
                              className='filter-base floating-input'
                              onChange={(e) => this.handleAuctionChange(e.target.value)} // This is correct now
                              disabled={loadingAuctions || !groupName} // Changed from loadingAuction
                            >
                              <option value="">Select Auction</option>
                              {loadingAuctions ? ( // Changed from loadingAuction
                                <option value="" disabled>Loading auctions...</option>
                              ) : !groupName ? (
                                <option value="" disabled>Select a group first</option>
                              ) : auctions.length === 0 ? (
                                <option value="" disabled>No auctions found for this group</option>
                              ) : (
                                auctions.map(auction => (
                                  <option key={auction.id} value={auction.id}>
                                    {auction.label}
                                  </option>
                                ))
                              )}
                            </select>
                            <label className="floating-label" >{i18n.t('auction_name')}</label>
                          </div>


                          {/* Status Filter */}
                          <div className="select-wrapper floating-label-wrapper">
                            <select
                              id="status"
                              name="status"
                              value={filters.status || ''}
                              onChange={(e) => this.handleFilterChange('status', e.target.value)}
                              className="filter-base floating-input"
                            >
                              <option value="">All Status</option>
                              <option value="P">Pending</option>
                              <option value="T">In Progress</option>
                              <option value="C">Completed</option>
                              <option value="D">Delayed</option>
                            </select>
                            <label htmlFor="status" className="floating-label">
                              Status
                            </label>
                          </div>



                          {/* <DateRangeSelector onChange={this.handleDateRangeChange} /> */}
                        </FilterSection>

                        {/* Summary Stats */}
                        <div className="metrics-grid">
                          <div className="metric-card">
                            <div className="metric-header">
                              <div className='metric-icon-wrapper blue'>
                                <Package />
                              </div>
                            </div>
                            <div className="metric-label">Total Sites</div>

                            <div className="metric-value" style={{ fontSize: "20px" }}>
                              {overallStats.total_sites || 0}
                            </div>
                          </div>


                          <div className="metric-card">
                            <div className="metric-header">
                              <div className='metric-icon-wrapper purple'>
                                <TrendingUp />
                              </div>
                            </div>
                            <div className="metric-label">Total Quantity (Tons)</div>
                            <div className="metric-value" style={{ fontSize: "20px" }}>
                              {formatWeight(overallStats.total_planned_weight)}
                            </div>
                          </div>

                          <div className="metric-card">
                            <div className="metric-header">
                              <div className='metric-icon-wrapper gray'>
                                <CheckCircle />
                              </div>
                            </div>

                            <div className="metric-label">Overall Progress</div>
                            <div className="metric-value" style={{ fontSize: "20px" }}>
                              {overallStats.average_progress?.toFixed(1) || 0}%
                            </div>
                          </div>

                          <div className="metric-card">
                            <div className="metric-header">
                              <div className='metric-icon-wrapper orange'>
                                <Truck />
                              </div>
                            </div>

                            <div className="metric-label">Active Sites</div>
                            <div className="metric-value" style={{ fontSize: "20px" }}>
                              {activeSites}
                            </div>
                          </div>
                        </div>
                      </div>



                      {/* Error Display */}
                      {error && (
                        <div className="error-message">
                          <AlertCircle size={20} />
                          <span>{error}</span>
                          <button onClick={this.fetchLoadingProgress}>Retry</button>
                        </div>
                      )}

                      {/* Sites Grid */}
                      {loading ? (
                        this.renderSkeletonLoader()
                      ) : (
                        <>
                          <div className="sites-grid">
                            {sites.length > 0 ? (
                              sites.map(site => (
                                <div
                                  key={site.id}
                                  onClick={() => featureFlags.showModal && this.setState({ selectedSite: site })}
                                  className="site-card"
                                  style={{ cursor: featureFlags.showModal ? 'pointer' : 'default' }}
                                >
                                  <div className={`status-indicator ${this.getStatusClass(site.status?.code)}`} />

                                  <div className="site-card-header">
                                    <div>
                                      <h3 className="site-name">{site.project_name || 'Unknown Site'}</h3>
                                      <div className="site-location">
                                        <MapPin size={14} />
                                        <span>{site.location || 'No location'}</span>
                                      </div>
                                    </div>
                                    {featureFlags.showStatusBadges && (
                                      <div className={`status-badge ${this.getStatusClass(site.status?.code)}`}>
                                        {this.getStatusIcon(site.status?.code)}
                                        {this.getStatusDisplay(site.status?.code)}
                                      </div>
                                    )}
                                  </div>

                                  {/* Truck Loading Visual */}
                                  {featureFlags.showTruckAnimation && (
                                    <div className="truck-container">
                                      <svg viewBox="0 0 230 120" className="truck-svg" width="100%">
                                        <g className="truck">
                                          <rect x="10" y="60" width="40" height="35" fill="#2c3e50" rx="3" />
                                          <rect x="15" y="65" width="15" height="15" fill="#3498db" opacity="0.7" />
                                          <rect x="35" y="65" width="10" height="15" fill="#3498db" opacity="0.7" />
                                          <rect x="55" y="50" width="150" height="45" fill="#1b8f9b" rx="3" />
                                          <rect
                                            x="55"
                                            y="50"
                                            width={150 * (Math.min(site.metrics?.progress_percentage || 0, 100) / 100)}
                                            height="45"
                                            fill="#10b981"
                                            rx="3"
                                          />
                                          <line x1="75" y1="50" x2="75" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                          <line x1="105" y1="50" x2="105" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                          <line x1="135" y1="50" x2="135" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                          <line x1="165" y1="50" x2="165" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                          <line x1="195" y1="50" x2="195" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                          <circle cx="35" cy="100" r="8" fill="#2c3e50" />
                                          <circle cx="35" cy="100" r="5" fill="#7f8c8d" />
                                          <circle cx="85" cy="100" r="8" fill="#2c3e50" />
                                          <circle cx="85" cy="100" r="5" fill="#7f8c8d" />
                                          <circle cx="175" cy="100" r="8" fill="#2c3e50" />
                                          <circle cx="175" cy="100" r="5" fill="#7f8c8d" />
                                        </g>
                                        <text x="130" y="77" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="bold">
                                          {Math.min(site.metrics?.progress_percentage || 0, 100).toFixed(0)}%
                                        </text>
                                      </svg>
                                    </div>
                                  )}

                                  {/* Site Info */}
                                  <div className="site-info-grid">
                                    <div className="info-item-loading">
                                      <div className="info-label">Collected</div>
                                      <div className="align-items-center">
                                        <div className="info-value">
                                          {formatWeight(site.metrics?.collected_weight)}
                                          <div className="info-unit">tons</div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="info-item-loading">
                                      <div className="info-label">Planned</div>
                                      <div className="align-items-center">
                                        <div className="info-value">
                                          {formatWeight(site.metrics?.planned_weight)}
                                          <div className="info-unit">tons</div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Footer Info */}
                                  <div className="site-card-footer">
                                    {featureFlags.showLastActivity && (
                                      <div className="last-activity">
                                        <Clock size={14} />
                                        <span>{site.last_activity || 'No activity'}</span>
                                      </div>
                                    )}
                                    {featureFlags.showModal && (
                                      <div className="view-details">
                                        View Details
                                        <ChevronRight size={16} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="no-data">
                                <Package size={48} className="no-data-icon" />
                                <p>No sites found matching your criteria.</p>
                                <button onClick={this.clearFilters} className="clear-filters-btn">
                                  Clear Filters
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Pagination */}
                          {pagination.last_page > 1 && (
                            <div className="pagination">
                              <button
                                onClick={() => this.handlePageChange(pagination.current_page - 1)}
                                disabled={pagination.current_page === 1}
                                className="pagination-btn"
                              >
                                Previous
                              </button>
                              <span className="pagination-info">
                                Page {pagination.current_page} of {pagination.last_page}
                              </span>
                              <button
                                onClick={() => this.handlePageChange(pagination.current_page + 1)}
                                disabled={pagination.current_page === pagination.last_page}
                                className="pagination-btn"
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {/* Detail Modal */}
                      {featureFlags.showModal && selectedSite && (
                        <div className="modal-overlay" onClick={() => this.setState({ selectedSite: null })}>
                          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header-loading">
                              <div className="modal-header-content">
                                <div>
                                  <h2 className="modal-title">{selectedSite.project_name || 'Site Details'}</h2>
                                  <div className="modal-lot-ref">Lot: {selectedSite.lot_ref}</div>
                                  <div className="modal-location">
                                    <MapPin size={16} />
                                    <span>{selectedSite.location} → {selectedSite.destination}</span>
                                  </div>
                                </div>
                                <button onClick={() => this.setState({ selectedSite: null })} className="close-button">
                                  <X size={24} />
                                </button>
                              </div>

                              <div className="modal-summary">
                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Progress</div>
                                  <div className="summary-item-value">
                                    {Math.min(selectedSite.metrics?.progress_percentage || 0, 100).toFixed(1)}%
                                  </div>
                                </div>


                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Collected (Tons)</div>
                                  <div className="summary-item-value">
                                    {parseFloat(String(selectedSite.metrics?.collected_weight).replace(/,/g, '')) / 1000} Tons
                                  </div>
                                </div>

                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Remaining</div>
                                  <div className="summary-item-value">
                                    {parseFloat(String(selectedSite.metrics?.remaining_weight).replace(/,/g, '')) / 1000} Tons
                                  </div>
                                </div>
                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Days Remaining</div>
                                  <div className="summary-item-value">
                                    {selectedSite.dates?.days_remaining || 0}
                                  </div>
                                </div>
                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Vehicles</div>
                                  <div className="summary-item-value">
                                    {selectedSite.metrics?.active_vehicles || 0}
                                  </div>
                                </div>
                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Yards</div>
                                  <div className="summary-item-value">
                                    {selectedSite.metrics?.yards_total || 0}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="modal-body">
                              {/* Stakeholders Section */}
                              <div className="stakeholders-section">
                                {/* <h4>Stakeholders</h4> */}
                                <div className="stakeholders-grid">
                                  {selectedSite.stakeholders?.buyer && (
                                    <div className="stakeholder-item">
                                      <strong>Buyer:</strong> {selectedSite.stakeholders.buyer.name}
                                    </div>
                                  )}
                                  {selectedSite.stakeholders?.material && (
                                    <div className="stakeholder-item">
                                      <strong>Material:</strong> {selectedSite.stakeholders.material.name}
                                    </div>
                                  )}
                                  {selectedSite.stakeholders?.supervisor && (
                                    <div className="stakeholder-item">
                                      <strong>Supervisor:</strong> {selectedSite.stakeholders.supervisor.name}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Yards Section */}
                              {selectedSite.breakdowns?.yards && selectedSite.breakdowns.yards.length > 0 && (
                                <div className="yards-section">
                                  <h4>Yards</h4>
                                  <div className="yards-grid">
                                    {selectedSite.breakdowns.yards.map((yard, index) => (
                                      <div key={index} className="yard-card">
                                        <div className="yard-header">
                                          <h5 className="yard-name">{yard.name}</h5>
                                          <span className={`yard-status ${yard.status}`}>{yard.status}</span>
                                        </div>
                                        <div className="yard-details">
                                          <div className="yard-weight">
                                            Collected:
                                            <b>{yard.collected_weight}</b> KG
                                            <span className="tons-conversion" style={{ color: '#999', marginLeft: '5px', fontSize: '0.9em' }}>
                                              ({parseFloat(String(yard.collected_weight).replace(/,/g, '')) / 1000} Tons)
                                            </span>
                                          </div>
                                          {/* {featureFlags.showProgressBars && (
                                            <div className="yard-progress-container">
                                              <div className="yard-progress-bar">
                                                <div
                                                  className={`yard-progress-fill ${yard.status}`}
                                                  style={{ width: `${Math.min(yard.progress || 0, 100)}%` }}
                                                />
                                              </div>
                                              <span className="yard-progress-text">{yard.progress || 0}%</span>
                                            </div>
                                          )} */}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Vehicles Section */}
                              {selectedSite.breakdowns?.vehicles && selectedSite.breakdowns.vehicles.length > 0 && (
                                <div className="vehicles-section">
                                  <h4>Assigned Vehicles</h4>
                                  <div className="vehicles-grid">
                                    {selectedSite.breakdowns.vehicles.map((vehicle, index) => (
                                      <div key={index} className="vehicle-card">
                                        <div className="vehicle-header">
                                          <span className="vehicle-number">{vehicle.vehicle_number}</span>
                                          <span className={`vehicle-status ${vehicle.status}`}>{vehicle.status}</span>
                                        </div>
                                        <div className="vehicle-details">
                                          <div>Model: {vehicle.model}</div>
                                          {/* <div>Capacity: {parseFloat(String(vehicle.capacity).replace(/,/g, '')) / 1000} Tons</div> */}


                                          {vehicle.driver && (
                                            <div>Driver: {vehicle.driver.name}</div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Recent Tickets */}
                              {selectedSite.breakdowns?.recent_tickets && selectedSite.breakdowns.recent_tickets.length > 0 && (
                                <div className="tickets-section">
                                  <h4>Recent Tickets</h4>
                                  <div className="tickets-table">
                                    <table>
                                      <thead>
                                        <tr>
                                          <th>Ticket #</th>
                                          <th>Net Weight</th>
                                          <th>Status</th>
                                          <th>Date</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {selectedSite.breakdowns.recent_tickets.map((ticket, index) => (
                                          <tr key={index}>
                                            <td>{ticket.ticket_number}</td>
                                            <td>{parseFloat(String(ticket.net_weight).replace(/,/g, '')) / 1000} Tons</td>
                                            <td>
                                              <span className={`ticket-status ${ticket.status?.toLowerCase()}`}>
                                                {ticket.status}
                                              </span>
                                            </td>
                                            <td>{new Date(ticket.created_at).toLocaleDateString()}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Export Buttons */}
                              {featureFlags.showExportButtons && (
                                <div className="action-buttons">
                                  {/* <button className="action-button action-button-primary">
                                    <FileText size={16} />
                                    Generate Report
                                  </button> */}
                                  <button className="action-button action-button-secondary" onClick={() => this.exportToExcel(selectedSite.id)}>
                                    <Download size={16} />
                                    Export to Excel
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Feature Flags Settings Panel */}
                      {showFlagSettings && (
                        <div className="feature-flags-overlay" onClick={() => this.setState({ showFlagSettings: false })}>
                          <div className="feature-flags-panel" onClick={(e) => e.stopPropagation()}>
                            <div className="feature-flags-header">
                              <h3>Feature Flags</h3>
                              <button onClick={() => this.setState({ showFlagSettings: false })} className="close-button-small">
                                <X size={20} />
                              </button>
                            </div>
                            <div className="feature-flags-body">
                              {Object.keys(featureFlags).map(flag => (
                                <div key={flag} className="flag-item">
                                  <label className="flag-label">
                                    {flag.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                  </label>
                                  <button
                                    onClick={() => this.toggleFeatureFlag(flag)}
                                    className={`flag-toggle ${featureFlags[flag] ? 'active' : ''}`}
                                  >
                                    <span className="toggle-slider"></span>
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="feature-flags-footer">
                              <button onClick={this.resetFeatureFlags} className="reset-button">
                                Reset to Defaults
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default LoadingProgressDashboard;